import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { ROLE_NAMES } from '../constants/roles';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ShieldAlert, Home, ArrowLeft } from 'lucide-react';

/**
 * Unauthorized access page
 * Shown when user tries to access a route they don't have permission for
 */
const Unauthorized = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, isAdmin, isTeacher, isStudent } = usePermissions();

  // Get the attempted route from location state
  const attemptedRoute = location.state?.from || 'this page';

  // Determine user's home page based on role
  const getHomePage = () => {
    if (isAdmin) return '/admin/dashboard';
    if (isTeacher) return '/teacher/dashboard';
    if (isStudent) return '/student/home';
    return '/student/home'; // Default fallback
  };

  const handleGoHome = () => {
    navigate(getHomePage(), { replace: true });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-destructive/10 rounded-full">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Access Denied</CardTitle>
          <CardDescription className="text-base mt-2">
            You don't have permission to access this page
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold">Your role:</span>{' '}
              <span className="text-foreground">{ROLE_NAMES[userRole] || 'Unknown'}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold">Attempted to access:</span>{' '}
              <span className="text-foreground font-mono text-xs">{attemptedRoute}</span>
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              This page requires higher privileges. If you believe this is an error, please contact your
              administrator.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleGoBack} variant="outline" className="flex-1">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
            <Button onClick={handleGoHome} className="flex-1">
              <Home className="mr-2 h-4 w-4" />
              Go to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Unauthorized;
